import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {map, Subject} from 'rxjs';
import {ResponseModel} from "@shared/models/response.model";
import { MenuCategoryModel } from '@shared/models/menu-category.model';
import { OfferModel } from '@shared/models/offer.model';
import { getCurrentContextIds } from '@shared/utils/current-context.util';

interface MenuCategoriesResponseData {
  categories: MenuCategoryModel[];
  offers?: OfferModel[];
}

export type MenuCategoriesResponse = ResponseModel<MenuCategoryModel[]> & {
  offers: OfferModel[];
};

@Injectable({providedIn: 'root'})
export class MenuCategoriesService {
  private update = new Subject<string>();
  constructor(private http: HttpClient) {}

  getAll(page = 0,
         perPage = 5,
         sort: string | null = null,
         filterKeyword: string | null = null,
         role: string | null = null
  ) {
    let params = new HttpParams()
      .set('page', page)
      .set('per_page', perPage);
    if (sort !== null) {
      params = params.set('sort', sort);
    }
    if (role !== null) {
      params = params.set('filter[role]', role);
    }

    if (filterKeyword !== null) {
      params = params.set('filter[search]', filterKeyword);
    }
    const { restaurantId, branchId, activeMenuId } = this.getCurrentMenuContext();
    return this.http
      .get<ResponseModel<MenuCategoryModel[] | MenuCategoriesResponseData>>(`restaurants/${restaurantId}/branches/${branchId}/menus/${activeMenuId}/menu-categories` , {params})
      .pipe(map(response => this.normalizeCategoriesResponse(response)));
  }

  addMenuCategory(menuCategory: object) {
    const { restaurantId, branchId, activeMenuId } = this.getCurrentMenuContext();
    return this.http.post<ResponseModel<MenuCategoryModel>>(`restaurants/${restaurantId}/branches/${branchId}/menus/${activeMenuId}/menu-categories`, menuCategory);
  }

  getMenuCategoryByID(id: string) {
    const { restaurantId, branchId, activeMenuId } = this.getCurrentMenuContext();
    return this.http.get<ResponseModel<MenuCategoryModel>>(`restaurants/${restaurantId}/branches/${branchId}/menus/${activeMenuId}/menu-categories/${id}`);
  }

  editMenuCategory(id: string, menuCategory: object) {
    const { restaurantId, branchId, activeMenuId } = this.getCurrentMenuContext();
    return this.http.post<ResponseModel<MenuCategoryModel>>(`restaurants/${restaurantId}/branches/${branchId}/menus/${activeMenuId}/menu-categories/${id}?_method=PUT`, menuCategory);
  }

  deleteMenuCategory(id: string) {
    const { restaurantId, branchId, activeMenuId } = this.getCurrentMenuContext();
    return this.http.delete(`restaurants/${restaurantId}/branches/${branchId}/menus/${activeMenuId}/menu-categories/${id}`);
  }

  sendUpdate(message: string) {
    this.update.next(message);
  }

  getUpdate() {
    return this.update.asObservable();
  }

  private normalizeCategoriesResponse(
    response: ResponseModel<MenuCategoryModel[] | MenuCategoriesResponseData>
  ): MenuCategoriesResponse {
    const data = response.data;
    const categories = Array.isArray(data) ? data : data?.categories ?? [];
    const offers = Array.isArray(data) ? [] : data?.offers ?? [];

    return {
      ...response,
      data: categories,
      offers,
    };
  }

  private getCurrentMenuContext() {
    const { restaurantId, branchId } = getCurrentContextIds();
    const branch = JSON.parse(localStorage.getItem('branch') ?? 'null');

    return {
      restaurantId,
      branchId,
      activeMenuId: branch?.active_menu?.id,
    };
  }
}
